/**
 * Step 1 : Informations générales
 * Société, Type, Nom, Véhicule, Template, Diagnostic, Diffusion
 */

import { useEffect, useMemo } from 'react';
import { AlertCircle, Building2, Info } from 'lucide-react';
import { Tooltip } from '@/Components/ui/Tooltip';
import { WarrantyFormState } from '@/features/warranties/hooks/useWarrantyFormWizard';
import { WARRANTY_TYPE_LABELS, WARRANTY_TYPES, VEHICLE_TYPES, VEHICLE_TYPE_LABELS } from '@/features/warranties/types/warranty.types';
import { useAllowedVehicleTypes } from '@/features/warranties/hooks/useAllowedVehicleTypes';
import { useSocietyVendorType } from '@/features/societies/hooks/useSocietyVendorType';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { getWarrantyTypeWording } from '@/utils/vendorWording';
import { useAuth } from '@/hooks/auth/useAuth';
import { FormInput } from '@/Components/shared/FormInput';
import { FormTextarea } from '@/Components/shared/FormTextarea';
import { AutocompleteSelect } from '@/Components/shared/AutocompleteSelect';
import { CardSelectInput, type CardSelectOption } from '@/Components/shared/CardSelectInput';
import type { SocietyListItem } from '@/features/societies/types/society.types';

interface Step1Props {
  formData: WarrantyFormState;
  updateFormData: (updates: Partial<WarrantyFormState>) => void;
  isSocietyLocked?: boolean;
  onCanProceedChange: (canProceed: boolean) => void;
  showErrors?: boolean;
}

export const Step1Informations = ({ formData, updateFormData, isSocietyLocked = false, onCanProceedChange, showErrors = false }: Step1Props) => {
  const { user } = useAuth();
  const isExternalUser = useMemo(() => user?.type === 'EXTERNAL', [user?.type]);
  const { canChooseWarrantyType, autoWarrantyType } = useSocietyVendorType(formData.societyId);
  const { allowedVehicleTypes, autoVehicleType, isSingleType } = useAllowedVehicleTypes(formData.societyId);
  const { data: effectiveConfig } = useEffectiveConfiguration(formData.societyId);

  // Calculer si le sélecteur de société doit être affiché
  const showSocietySelector = useMemo(() => {
    return user?.type === 'INTERNAL' && !isSocietyLocked;
  }, [user?.type, isSocietyLocked]);

  // Options de type de garantie
  const warrantyTypeOptions = useMemo<CardSelectOption<number>[]>(() => {
    const options: CardSelectOption<number>[] = [
      {
        label: WARRANTY_TYPE_LABELS[WARRANTY_TYPES.WARRANTY],
        value: WARRANTY_TYPES.WARRANTY,
        description: 'Garantie proposée par un vendeur propriétaire des véhicules',
      },
      {
        label: WARRANTY_TYPE_LABELS[WARRANTY_TYPES.SERVICE],
        value: WARRANTY_TYPES.SERVICE,
        description: 'Prestation proposée par un prestataire de service',
      },
    ];
    if (effectiveConfig?.warrantyExtension) {
      options.push({
        label: WARRANTY_TYPE_LABELS[WARRANTY_TYPES.EXTENSION],
        value: WARRANTY_TYPES.EXTENSION,
        description: 'Extension de la garantie constructeur du véhicule',
      });
    }
    return options;
  }, [effectiveConfig?.warrantyExtension]);

  // Options de type de véhicule (filtrées par la configuration société)
  const allVehicleTypeOptions = useMemo<CardSelectOption<number>[]>(() => [
    {
      label: VEHICLE_TYPE_LABELS[VEHICLE_TYPES.STANDARD],
      value: VEHICLE_TYPES.STANDARD,
      description: 'Véhicules particuliers standard',
    },
    {
      label: VEHICLE_TYPE_LABELS[VEHICLE_TYPES.COLLECTION],
      value: VEHICLE_TYPES.COLLECTION,
      description: 'Véhicules de collection et anciens',
    },
    {
      label: VEHICLE_TYPE_LABELS[VEHICLE_TYPES.TWO_WHEELER],
      value: VEHICLE_TYPES.TWO_WHEELER,
      description: 'Motos et scooters',
    },
  ], []);

  const vehicleTypeOptions = useMemo(
    () => allVehicleTypeOptions.filter((opt) => allowedVehicleTypes.includes(opt.value)),
    [allVehicleTypeOptions, allowedVehicleTypes],
  );

  // Effet de synchronisation : Définir automatiquement le type si imposé par le vendorType
  useEffect(() => {
    if (autoWarrantyType !== undefined && formData.warrantyType !== autoWarrantyType) {
      updateFormData({ warrantyType: autoWarrantyType });
    }
  }, [autoWarrantyType, formData.warrantyType, updateFormData]);

  // Effet de synchronisation : Définir automatiquement le type de véhicule si un seul autorisé
  useEffect(() => {
    if (autoVehicleType !== undefined && formData.vehicleType !== autoVehicleType) {
      updateFormData({ vehicleType: autoVehicleType });
    }
  }, [autoVehicleType, formData.vehicleType, updateFormData]);

  /**
   * Même garde-fou que sur la durée au Step 2 : un `vehicleType` posé par un
   * preset (choisi avant la société) ou par une session restaurée peut ne pas
   * figurer parmi les types autorisés par la config de la société. Aucune carte
   * n'apparaît alors sélectionnée alors que la valeur est bien présente.
   */
  const isVehicleTypeAllowed = useMemo(() => {
    if (formData.vehicleType === undefined) return true;
    return allowedVehicleTypes.includes(formData.vehicleType);
  }, [formData.vehicleType, allowedVehicleTypes]);

  // Validation dérivée
  const canProceed = useMemo(() => {
    return (
      !!formData.societyId &&
      formData.warrantyType !== undefined &&
      formData.name.length >= 3 &&
      formData.vehicleType !== undefined &&
      isVehicleTypeAllowed
    );
  }, [formData.societyId, formData.warrantyType, formData.name, formData.vehicleType, isVehicleTypeAllowed]);

  // Remonter la validité au container
  useEffect(() => {
    onCanProceedChange(canProceed);
  }, [canProceed, onCanProceedChange]);

  // Wording dynamique selon le type
  const wording = useMemo(() => getWarrantyTypeWording(formData.warrantyType), [formData.warrantyType]);
  const nameLabel = 'Nom du contrat';
  const namePlaceholder = formData.warrantyType === 1
    ? 'Ex: Assistance Dépannage Premium'
    : `Ex: ${wording.singular} Mécanique Premium`;

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-4">

      {/* Bloc unique: Informations */}
      <div className="tw-relative tw-p-6 tw-space-y-5">

        {/* Section: Société */}
        {showSocietySelector && (
          <>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
              <Building2 className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
              <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Société</h4>
            </div>
            <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
              <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
                Sélectionnez la société pour laquelle créer {wording.withArticle}
                <Tooltip content="Société pour laquelle le contrat sera créé" />
              </p>
              <div id="society-select">
                <AutocompleteSelect<SocietyListItem>
                  apiEndpoint="/societies"
                  searchParam="nameFilter"
                  getOptionLabel={(society) => society.name}
                  getOptionValue={(society) => society.uuid}
                  value={formData.societyId || null}
                  onChange={(value) => updateFormData({ societyId: value || undefined })}
                  label="Société"
                  required
                  placeholder="Rechercher une société..."
                  renderOption={(society) => (
                    <div>
                      <div className="tw-font-medium">{society.name}</div>
                      {society.rootParentName && (
                        <div className="tw-text-xs tw-text-gray-500">
                          Groupe : {society.rootParentName}
                        </div>
                      )}
                    </div>
                  )}
                  filterActive={true}
                />
                {showErrors && !formData.societyId && (
                  <p className="tw-mt-1 tw-text-sm tw-text-red-600">La société est obligatoire</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Message d'erreur uniquement si utilisateur externe sans societyId */}
        {user?.type === 'EXTERNAL' && !user?.societyId && (
          <div className="tw-flex tw-items-start tw-gap-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4">
            <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0" />
            <div>
              <p className="tw-text-sm tw-font-medium tw-text-red-900">
                Erreur de configuration
              </p>
              <p className="tw-text-sm tw-text-red-700 tw-mt-1">
                Aucune société associée à votre compte. Contactez un administrateur.
              </p>
            </div>
          </div>
        )}

        {/* Titre unique */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
          <Info className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Informations</h4>
        </div>

        {/* Bloc 1 : Nom */}
        <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
          <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
            Définissez le nom du contrat
            <Tooltip content="Nom commercial du contrat, visible par le client" />
          </p>
          <div id="warranty-name-input">
            <FormInput
              value={formData.name}
              onChange={(value) => updateFormData({ name: value.toUpperCase() })}
              label={nameLabel}
              placeholder={namePlaceholder}
              required
              valid={formData.name.length >= 3}
              maxLength={150}
              minLength={3}
              error={showErrors && formData.name.length < 3 ? 'Le nom est obligatoire (minimum 3 caractères)' : undefined}
            />
            <p className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-mb-0">
              {formData.name.length}/150 caractères (minimum 3)
            </p>
          </div>
        </div>

        {/* Bloc 2 : Type de garantie - Conditionnel selon vendorType */}
        {formData.societyId && canChooseWarrantyType && (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
              Sélectionnez le type de contrat
              <Tooltip content="Garantie pour les vendeurs propriétaires, Prestation pour les prestataires de service" />
            </p>
            <div id="warranty-type-select">
              <CardSelectInput<number>
                options={warrantyTypeOptions}
                value={formData.warrantyType}
                onChange={(value) => updateFormData({ warrantyType: value as 0 | 1 | 2 })}
                name="warrantyType"
                required
                columns={warrantyTypeOptions.length as 2 | 3}
              />
              {showErrors && formData.warrantyType === undefined && (
                <p className="tw-mt-1 tw-text-sm tw-text-red-600">Le type de contrat est obligatoire</p>
              )}
            </div>
          </div>
        )}

        {/* Bloc 3 : Type de véhicule — masqué si un seul type autorisé */}
        {!isSingleType && (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
              Sélectionnez le type de véhicule couvert
              <Tooltip content="Type de véhicule couvert par ce contrat" />
            </p>
            <div id="vehicle-type-select">
              <CardSelectInput<number>
                options={vehicleTypeOptions}
                value={formData.vehicleType}
                onChange={(value) => updateFormData({ vehicleType: value })}
                name="vehicleType"
                required
                columns={vehicleTypeOptions.length as 2 | 3}
              />
              {showErrors && formData.vehicleType === undefined && (
                <p className="tw-mt-1 tw-text-sm tw-text-red-600">Le type de véhicule est obligatoire</p>
              )}
              {!isVehicleTypeAllowed && (
                <p className="tw-mt-1 tw-text-sm tw-text-red-600">
                  Ce type de véhicule n'est pas couvert par cette société. Choisissez-en un ci-dessus.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Informations complémentaires (utilisateurs internes uniquement) */}
        {!isExternalUser && (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-space-y-4">
            <div>
              <p className="tw-text-sm tw-text-gray-500 tw-mb-0">
                Informations complémentaires
                <Tooltip content="Rédigez des phrases complètes décrivant les conditions particulières. Ce contenu sera repris dans le PDF du contrat." />
              </p>
              <p className="tw-text-xs tw-text-gray-400 tw-mt-1 tw-mb-0">
                Rédigez des phrases complètes. Le texte saisi (sauts de ligne inclus) sera repris dans le PDF du contrat.
              </p>
            </div>
            <FormTextarea
              value={formData.diagnostic || ''}
              onChange={(value) => updateFormData({ diagnostic: value || undefined })}
              label="Informations complémentaires"
              placeholder={`Informations complémentaires pour cette ${wording.singularLower}...`}
              rows={4}
            />
          </div>
        )}
      </div>

    </div>
  );
};
